import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { getFontAwesomeClassName } from "@/other/utils/utils";
import { RESOURCE_FONT_SIZE } from "@/other/constants/OtherKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/modern/components/ui/select";
import { Label } from "@/modern/components/ui/label";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { Pencil } from "lucide-react";

interface IconTagValue {
  id: string | number;
  tagSet: string;
  tagValue: string;
  order: number;
}

const IconDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user, userPreferences } = useSwivuBryntumContext();
  const [icons, setIcons] = useState<IconTagValue[]>([]);
  const [resourceIconSize, setResourceIconSize] = useState<string>("Small");
  const [editingIconId, setEditingIconId] = useState<string | number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user]);

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.resourceTagValuesStore,
  });

  const loadIcons = () => {
    if (!project) return;
    const tagValuesStore = project.getCrudStore(STORE_KEYS.resourceTagValuesStore);
    const iconRecords = tagValuesStore.query((record: any) => record.tagSet === TAGS_KEYS.icon, true) || [];
    setIcons(() => iconRecords as unknown as IconTagValue[]);
  };

  useEffect(() => {
    loadIcons();
    // Load resource icon size setting
    if (userPreferences) {
      const savedSize = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.resourceIconSize, "Small");
      setResourceIconSize(savedSize || "Small");
    }
    return subscribe(() => {
      loadIcons();
    });
  }, [subscribe, userPreferences]);

  const handleIconSizeChange = (value: string) => {
    setResourceIconSize(value);
    if (userPreferences) {
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.resourceIconSize, value);
    }
  };

  const isDuplicateIconValue = (value: string, excludeIconId?: string | number) => {
    const tagValuesStore = project.getCrudStore(STORE_KEYS.resourceTagValuesStore);
    const allIcons = tagValuesStore.query((record: any) => record.tagSet === TAGS_KEYS.icon, true) || [];
    return allIcons.some((i: any) => {
      if (excludeIconId !== undefined && i.id === excludeIconId) {
        return false;
      }
      return i.tagValue.toLowerCase() === value.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateIconValue(value)) {
      toast.error('Icon already exists');
      return;
    }

    const tagValuesStore = project.getCrudStore(STORE_KEYS.resourceTagValuesStore);
    const newIcon = tagValuesStore.createRecord({
      tagSet: TAGS_KEYS.icon,
      tagValue: value,
    });
    tagValuesStore.add(newIcon);
  };

  const handleReorderItems = (movedItem: IconTagValue, beforeItem: IconTagValue | null) => {
    const tagValuesStore = project.getCrudStore(STORE_KEYS.resourceTagValuesStore);
    // @ts-expect-error - tagValue is a TagValue model
    tagValuesStore.move(movedItem, beforeItem);
  };

  const handleUpdateIconValue = (icon: IconTagValue, newValue: string) => {
    if (!newValue.trim()) {
      return false;
    }

    if (isDuplicateIconValue(newValue, icon.id)) {
      toast.error('Icon already exists');
      return false;
    }

    icon.tagValue = newValue;
    return true;
  };

  const handleDeleteIcon = (icon: IconTagValue) => {
    const tagValuesStore = project.getCrudStore(STORE_KEYS.resourceTagValuesStore);
    // @ts-expect-error - icon is a TagValue model
    tagValuesStore.remove(icon);
  };

  const handleStartEdit = (icon: IconTagValue) => {
    setEditingIconId(icon.id);
    setEditingValue(icon.tagValue);
    // Focus the input after it renders
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const handleSaveEdit = (icon: IconTagValue) => {
    if (editingValue.trim()) {
      const success = handleUpdateIconValue(icon, editingValue);
      if (success) {
        setEditingIconId(null);
        setEditingValue("");
      }
    } else {
      setEditingIconId(null);
      setEditingValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingIconId(null);
    setEditingValue("");
  };

  const renderIconPreview = (iconValue: string) => {
    try {
      const className = getFontAwesomeClassName(iconValue);
      return <i className={`fa ${className} text-xl`} />;
    } catch {
      return <i className="fa fa-question-circle text-xl text-muted-foreground" />;
    }
  };

  return (
    <div className="p-6 max-w-3xl w-full h-full flex-col flex">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
          <p className="text-sm text-muted-foreground">
            {subcategory.description}
          </p>
        </div>
        {/* Resource Icon Size Selector */}
        <div className="mb-6 flex items-center gap-4">
          <Label htmlFor="icon-size" className="text-sm font-medium whitespace-nowrap">
            Resource Icon Size
          </Label>
          <Select
            value={resourceIconSize}
            onValueChange={handleIconSizeChange}
            disabled={!canEdit}
          >
            <SelectTrigger id="icon-size" className="w-40">
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(RESOURCE_FONT_SIZE).map((size) => (
                <SelectItem key={size} value={size}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <SwivuList.Root<IconTagValue>
        items={icons}
        onReorder={handleReorderItems}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<IconTagValue> emptyMessage="No icons yet. Create your first icon!">
          {(icon: IconTagValue) => (
            <SwivuList.Item value={icon.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={icon.id as string | number} />

              {/* Icon Preview */}
              <div className="relative flex items-center justify-start w-10 h-10 shrink-0 group flex-1">
                {editingIconId === icon.id ? (
                  <Input
                    ref={inputRef}
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={() => handleSaveEdit(icon)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveEdit(icon);
                      } else if (e.key === "Escape") {
                        handleCancelEdit();
                      }
                    }}
                    className="w-full h-full text-xs px-1"
                    disabled={!canEdit}
                  />
                ) : (
                  <div className="flex items-center gap-3">
                    {renderIconPreview(icon.tagValue)}

                    <Button
                      variant="ghost"
                      size="icon"
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      onClick={() => handleStartEdit(icon)}
                      disabled={!canEdit}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteIcon(icon)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new icon"
        />
      </SwivuList.Root>
    </div>
  );
};

export default IconDetail;
